<script setup lang="ts">
import { CalendarClock, ChevronDown, EllipsisVertical, Eye, History, Pencil, Plus, RefreshCw, Sparkles, Trash2 } from "@lucide/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { computed, ref } from "vue";
import { toast } from "vue-sonner";
import type { KnowledgeSource, SourceSuggestion } from "@/api/contracts";
import { useSourceSuggestions } from "@/api/setup";
import { createSource, deleteSource, reextractSource, syncSource, updateSource, useConnectors, useSourceRuns, useSources, type SourceInput } from "@/api/knowledge";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import JsonView from "@/components/common/JsonView.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import RelativeTime from "@/components/common/RelativeTime.vue";
import StatusBadge from "@/components/common/StatusBadge.vue";
import JobProgress from "@/components/jobs/JobProgress.vue";
import KnowledgeNav from "@/components/knowledge/KnowledgeNav.vue";
import SourceForm from "@/components/knowledge/SourceForm.vue";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { confirm } from "@/composables/useConfirm";
import { t } from "@/copy/strings";
import { describeCron } from "@/lib/cron";
import { errorMessage } from "@/lib/errors";
import { formatDuration } from "@/lib/format";

definePage({ meta: { title: "Knowledge sources", requires: { capability: "knowledge.admin" } } });

const queryClient = useQueryClient();
const sources = useSources();
const connectors = useConnectors();

const formOpen = ref(false);
const editing = ref<KnowledgeSource | null>(null);
const busy = ref(false);
const formError = ref<unknown>(null);
const runsFor = ref<KnowledgeSource | null>(null);
const runsOpen = computed({ get: () => Boolean(runsFor.value), set: (v) => !v && (runsFor.value = null) });
const runs = useSourceRuns(() => runsFor.value?.id ?? null);
const lastJob = ref<Record<string, string>>({});

const connectorLabel = (type: string) => connectors.data.value?.find((c) => c.type === type)?.label ?? type;

// Datasources that a connector could sync but no source reads yet.
const suggestions = useSourceSuggestions();
const initial = ref<SourceSuggestion | null>(null);
const creatingAll = ref(false);

function openCreate() {
  editing.value = null;
  initial.value = null;
  formError.value = null;
  formOpen.value = true;
}

function openSuggestion(s: SourceSuggestion) {
  editing.value = null;
  initial.value = s;
  formError.value = null;
  formOpen.value = true;
}

async function createAllSuggested() {
  const list = suggestions.data.value ?? [];
  const ok = await confirm({
    title: `Create ${list.length} knowledge source(s)?`,
    description: "One source per datasource, synced every 6 hours and readable by everyone with knowledge access. Restrict visibility afterwards where needed.",
    confirmLabel: t.common.create,
  });
  if (!ok) return;
  creatingAll.value = true;
  let created = 0;
  for (const s of list) {
    try {
      await createSource({ type: s.connectorType, name: s.name, config: s.config, schedule: "0 */6 * * *", visibility: [], extract: false, extractSkip: [] });
      created++;
    } catch (err) {
      toast.error(`${s.name}: ${errorMessage(err)}`);
    }
  }
  creatingAll.value = false;
  toast.success(`${created} source(s) created. Run a first sync to fill them.`);
  await queryClient.invalidateQueries({ queryKey: ["knowledge"] });
}
function openEdit(s: KnowledgeSource) {
  editing.value = s;
  formError.value = null;
  formOpen.value = true;
}

async function save(value: SourceInput) {
  busy.value = true;
  formError.value = null;
  try {
    if (editing.value) await updateSource(editing.value.id, { name: value.name, config: value.config, schedule: value.schedule ?? null, visibility: value.visibility, extract: value.extract, extractSkip: value.extractSkip, searchWeight: value.searchWeight });
    else await createSource(value);
    toast.success(editing.value ? "Source saved." : "Source added. Run a first sync to fill it.");
    formOpen.value = false;
    await queryClient.invalidateQueries({ queryKey: ["knowledge", "sources"] });
    await queryClient.invalidateQueries({ queryKey: ["knowledge", "source-suggestions"] });
  } catch (err) {
    formError.value = err;
  } finally {
    busy.value = false;
  }
}

async function toggle(s: KnowledgeSource, enabled: boolean) {
  try {
    await updateSource(s.id, { enabled });
    await queryClient.invalidateQueries({ queryKey: ["knowledge", "sources"] });
  } catch (err) {
    toast.error(errorMessage(err));
  }
}

async function sync(s: KnowledgeSource, mode: "incremental" | "full") {
  if (mode === "full") {
    const ok = await confirm({ title: `Full resync of ${s.name}?`, description: "Re-reads everything from the source. This can take a while; unchanged pages produce no new revisions." });
    if (!ok) return;
  }
  try {
    const { jobId } = await syncSource(s.id, mode);
    lastJob.value = { ...lastJob.value, [s.id]: jobId };
    toast.success(`Sync started for ${s.name}.`);
  } catch (err) {
    toast.error(errorMessage(err));
  }
}

async function reextract(s: KnowledgeSource) {
  const ok = await confirm({
    title: `Run AI suggestions again for ${s.name}?`,
    description:
      "Sends every document of this source to AI extraction again, with the current rules (new names wait until several pages mention them). Clear old suggestions in the Inbox first. Costs AI tokens.",
    confirmLabel: "Run again",
  });
  if (!ok) return;
  try {
    const { queued } = await reextractSource(s.id);
    toast.success(`${queued.toLocaleString()} documents queued for AI suggestions.`);
  } catch (err) {
    toast.error(errorMessage(err));
  }
}

async function remove(s: KnowledgeSource) {
  const ok = await confirm({ title: `Remove ${s.name}?`, description: "Stops syncing. Documents from this source are removed from search; knowledge pages written by people stay.", confirmLabel: t.common.remove, destructive: true, typeToConfirm: s.name });
  if (!ok) return;
  try {
    await deleteSource(s.id);
    toast.success("Source removed.");
    await queryClient.invalidateQueries({ queryKey: ["knowledge", "sources"] });
  } catch (err) {
    toast.error(errorMessage(err));
  }
}
</script>

<template>
  <div>
    <PageHeader :title="t.knowledge.sources" :description="t.knowledge.sourcesDescription">
      <template #actions>
        <Button :disabled="!connectors.data.value?.length" @click="openCreate"><Plus /> Add source</Button>
      </template>
    </PageHeader>
    <KnowledgeNav />

    <details v-if="suggestions.data.value?.length" class="mb-5 rounded-lg border border-dashed p-4" aria-label="Suggested sources">
      <summary class="cursor-pointer text-sm font-medium">{{ suggestions.data.value.length }} datasource(s) without a knowledge source</summary>
      <p class="mt-2 text-sm text-muted-foreground">Infrastructure datasources can document themselves: servers, sites, clusters, workloads, zones and resources become knowledge entities with their lineage and environment.</p>
      <ul class="mt-3 flex flex-col gap-2">
        <li v-for="s in suggestions.data.value" :key="`${s.connectorType}/${s.datasource}`" class="flex flex-wrap items-center gap-2 text-sm">
          <span class="font-medium">{{ s.connectorLabel }}</span>
          <span class="font-mono text-xs text-muted-foreground">{{ s.datasourceType }}/{{ s.datasource }}</span>
          <span v-if="s.targets.length" class="text-xs text-muted-foreground">{{ s.targets.join(", ") }}</span>
          <Button size="xs" variant="outline" class="ml-auto" @click="openSuggestion(s)"><Plus /> Add</Button>
        </li>
      </ul>
      <Button class="mt-3" size="sm" :disabled="creatingAll" @click="createAllSuggested"><Plus /> Create all ({{ suggestions.data.value.length }})</Button>
    </details>

    <ListSkeleton v-if="sources.isPending.value" :rows="3" />
    <ErrorState v-else-if="sources.error.value" :error="sources.error.value" @retry="sources.refetch()" />
    <EmptyState v-else-if="!sources.data.value?.length" :icon="RefreshCw" :title="t.knowledge.sourcesEmpty">
      <Button :disabled="!connectors.data.value?.length" @click="openCreate"><Plus /> Add source</Button>
    </EmptyState>
    <ul v-else class="flex flex-col gap-3">
      <Card v-for="s in sources.data.value" :key="s.id" as="li" padding="none" class="gap-3 p-4">
        <!-- Phones: title and facts first, the actions wrap below them (UI-01) -->
        <div class="flex flex-col gap-3 sm:flex-row sm:items-start">
          <div class="min-w-0 grow sm:basis-64">
            <p class="font-medium break-words">{{ s.name }}</p>
            <p class="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
              <span>{{ connectorLabel(s.type) }}</span>
              <span class="inline-flex items-center gap-1" :title="s.schedule ? `UTC cron ${s.schedule}` : undefined">
                <CalendarClock class="size-3.5 shrink-0" aria-hidden="true" />{{ s.schedule ? describeCron(s.schedule) : t.knowledge.syncManual }}
              </span>
              <span class="inline-flex items-center gap-1">
                <Eye class="size-3.5 shrink-0" aria-hidden="true" />{{ s.visibility.length ? t.knowledge.readableBy(s.visibility.join(", ")) : t.knowledge.readableByEveryone }}
              </span>
              <span v-if="s.extract" class="inline-flex items-center gap-1"><Sparkles class="size-3.5 shrink-0" aria-hidden="true" />{{ t.knowledge.aiSuggestionsOn }}</span>
            </p>
            <p class="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span class="whitespace-nowrap">Last sync <RelativeTime :value="s.lastSyncAt" /></span>
              <StatusBadge v-if="s.lastStatus" :status="s.lastStatus" />
            </p>
          </div>
          <div class="flex shrink-0 flex-wrap items-center gap-2 sm:gap-3">
            <label class="mr-auto flex items-center gap-2 text-sm sm:mr-0">
              <Switch :model-value="s.enabled" :aria-label="`${s.name} enabled`" @update:model-value="(v: boolean) => toggle(s, v)" />
              {{ s.enabled ? t.common.enabled : t.common.disabled }}
            </label>
            <DropdownMenu>
              <DropdownMenuTrigger as-child>
                <Button variant="outline" size="sm"><RefreshCw /> {{ t.knowledge.syncNow }} <ChevronDown /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem @select="sync(s, 'incremental')">Changes since last sync</DropdownMenuItem>
                <DropdownMenuItem @select="sync(s, 'full')">Everything (full resync)</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger as-child>
                <Button variant="ghost" size="icon-sm" :aria-label="`More actions for ${s.name}`"><EllipsisVertical /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem @select="runsFor = s"><History /> {{ t.knowledge.runs }}</DropdownMenuItem>
                <DropdownMenuItem @select="openEdit(s)"><Pencil /> {{ t.common.edit }}</DropdownMenuItem>
                <DropdownMenuItem v-if="s.extract" @select="reextract(s)"><Sparkles /> Run AI suggestions again…</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem class="text-destructive" @select="remove(s)"><Trash2 /> {{ t.common.remove }}…</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <JobProgress v-if="lastJob[s.id]" :job-id="lastJob[s.id]" />
      </Card>
    </ul>

    <Sheet v-model:open="formOpen">
      <SheetContent side="right" class="w-full gap-0 overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle class="pr-8">{{ editing ? `Edit ${editing.name}` : "Add a knowledge source" }}</SheetTitle>
          <SheetDescription>Secrets are stored encrypted and never shown again.</SheetDescription>
        </SheetHeader>
        <div class="flex flex-1 flex-col px-4">
          <SourceForm
            v-if="formOpen && connectors.data.value"
            :key="editing?.id ?? `new-${initial?.connectorType ?? ''}-${initial?.datasource ?? ''}`"
            :connectors="connectors.data.value"
            :source="editing"
            :initial="initial ? { type: initial.connectorType, name: initial.name, config: initial.config } : null"
            :busy="busy"
            :error="formError"
            @submit="save"
            @cancel="formOpen = false"
          />
        </div>
      </SheetContent>
    </Sheet>

    <Sheet v-model:open="runsOpen">
      <SheetContent side="right" class="w-full overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>{{ t.knowledge.runs }}: {{ runsFor?.name }}</SheetTitle>
          <SheetDescription>Recent syncs, newest first.</SheetDescription>
        </SheetHeader>
        <div class="flex flex-col gap-3 px-4 pb-6">
          <ListSkeleton v-if="runs.isPending.value" :rows="3" />
          <ErrorState v-else-if="runs.error.value" compact :error="runs.error.value" @retry="runs.refetch()" />
          <p v-else-if="!runs.data.value?.length" class="text-sm text-muted-foreground">No runs yet.</p>
          <div v-for="r in runs.data.value" :key="r.id" class="flex flex-col gap-2 rounded-lg border p-3">
            <div class="flex items-center gap-2 text-sm">
              <StatusBadge :status="r.status" />
              <span class="font-medium">{{ r.mode }}</span>
              <span class="ml-auto text-xs text-muted-foreground">
                <RelativeTime :value="r.startedAt" /><template v-if="r.finishedAt"> · {{ formatDuration(new Date(r.finishedAt).getTime() - new Date(r.startedAt).getTime()) }}</template>
              </span>
            </div>
            <p v-if="r.error" class="rounded bg-destructive-soft px-2 py-1 text-xs text-destructive">{{ r.error }}</p>
            <JsonView v-if="r.stats" :value="r.stats" :max-height="120" />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  </div>
</template>
